import { Avatar, Box, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import moment from "moment-timezone";
import CreatableSelect from "react-select/creatable";
import { removeUnderscore } from "../../../utils/removedUnderScore";

const RightSideBasicInfo = ({ records, mergedData, handleFieldChange }) => {
  // Helper function to get person name from record
  const getPersonName = (record) => {
    return (
      `${record.FIRST_NAME || ""} ${record.LAST_NAME || ""}`.trim() || "Unknown"
    );
  };

  const CustomOption = ({ data, innerRef, innerProps }) => {
    console.log("=====data", data);
    return (
      <div
        ref={innerRef}
        {...innerProps}
        style={{ padding: "8px", cursor: "pointer" }}
      >
        <Flex alignItems="center" gap={2}>
          <Avatar
            size="xs"
            name={data.sourceName}
            bg={data.avatarColor || "blue.500"}
            color="white"
            fontSize="10px"
          />
          <Box>
            <Text fontSize="13px" fontWeight="500">
              {data.displayValue}
            </Text>
            <Text fontSize="11px" color="gray.500">
              {data.sourceName}
            </Text>
          </Box>
        </Flex>
      </div>
    );
  };

  const CustomSingleValue = ({ data }) => (
    <Flex alignItems="center" gap={2}>
      <Box
        w="10px"
        h="10px"
        borderRadius="full"
        bg={data.avatarColor || "gray.500"}
      />
      <Text fontSize="13px">{data.displayValue}</Text>
    </Flex>
  );

  // Custom multi value label component
  const CustomMultiValueLabel = ({ data }) => (
    <Flex alignItems="center" gap={1}>
      <Box
        w="8px"
        h="8px"
        borderRadius="full"
        bg={data.avatarColor || "gray.500"}
      />
      <Text fontSize="12px">{data.displayValue}</Text>
    </Flex>
  );

  return (
    <Box>
      <SimpleGrid columns={4} spacing={1}>
        {Object.keys(records[0])
          .filter(
            (key) =>
              ![
                "ADDRESS",
                "HISTORY",
                "id",
                "START_DATE",
                "CREATE_DATE",
                "TYPE",
                "_rid",
                "_self",
                "_etag",
                "_attachments",
                "_ts",
                "Type",
                "DEDUPLICATION",
                "DEDUPLICATION_ID",
                "SOURCE_PROFILES",
                "avatarColor",
              ].includes(key)
          )
          .map((item, idx) => {
            let isMultiSelect = Array.isArray(records[0][item]);

            // Build options with source information
            let selectOptions = [];

            records.forEach((rec) => {
              const personName = getPersonName(rec);
              const avatarColor = rec.avatarColor;
              const values = isMultiSelect ? rec[item] || [] : [rec[item]];

              values.forEach((value) => {
                if (value !== undefined && value !== "") {
                  let displayValue = value;

                  // Handle BIRTH_DATE formatting
                  if (item === "BIRTH_DATE") {
                    displayValue = moment(value, "YYYYMMDD").format(
                      "MM/DD/YYYY"
                    );
                  }

                  // Check if this value already exists in options
                  const existingOption = selectOptions.find(
                    (opt) => opt.value === value
                  );

                  if (existingOption) {
                    if (!existingOption.sources.includes(personName)) {
                      existingOption.sources.push(personName);
                      existingOption.sourceName =
                        existingOption.sources.join(", ");

                      existingOption.avatarColor = undefined;
                    }
                  } else {
                    selectOptions.push({
                      label: displayValue,
                      value: value,
                      displayValue: displayValue,
                      sourceName: personName,
                      sources: [personName],
                      avatarColor: avatarColor,
                    });
                  }
                }
              });
            });

            return (
              <Flex alignItems="center" key={idx} fontSize="sm">
                <Box textAlign="left" width="100%" ml={4}>
                  <Box>
                    <Text
                      fontSize="12px"
                      fontWeight={500}
                      mb={1}
                      color={getFieldColor(records, item, isMultiSelect)}
                    >
                      {removeUnderscore(item)}
                    </Text>
                  </Box>

                  {isMultiSelect ? (
                    // Multi-select dropdown using react-select
                    <CreatableSelect
                      isMulti
                      options={selectOptions}
                      value={mergedData[item]?.map((val) => {
                        const option = selectOptions.find(
                          (opt) => opt.value === val
                        );
                        return (
                          option || {
                            label: val,
                            value: val,
                            displayValue: val,
                          }
                        );
                      })}
                      onChange={(selectedOptions) =>
                        handleFieldChange(
                          item,
                          selectedOptions.map((option) => option.value) // Only save the value
                        )
                      }
                      components={{
                        Option: CustomOption,
                        MultiValueLabel: CustomMultiValueLabel,
                      }}
                      placeholder="Select..."
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          fontSize: "13px",
                          padding: "0px",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          fontSize: "13px",
                        }),
                      }}
                    />
                  ) : (
                    // Standard single-select dropdown
                    <CreatableSelect
                      options={selectOptions}
                      value={
                        item === "BIRTH_DATE"
                          ? selectOptions.find(
                              (opt) => opt.value === mergedData[item]
                            ) || null
                          : selectOptions.find(
                              (opt) => opt.value === mergedData[item]
                            ) || null
                      }
                      onChange={(selectedOption) =>
                        handleFieldChange(
                          item,
                          selectedOption ? selectedOption.value : "" // Only save the value
                        )
                      }
                      components={{
                        Option: CustomOption,
                        SingleValue: CustomSingleValue,
                      }}
                      placeholder="Select..."
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          fontSize: "13px",
                        }),
                        menu: (provided) => ({
                          ...provided,
                          fontSize: "13px",
                        }),
                      }}
                    />
                  )}
                </Box>
              </Flex>
            );
          })}
      </SimpleGrid>
    </Box>
  );
};

export default RightSideBasicInfo;

const getFieldColor = (records, item, isMultiSelect) => {
  const values = records.map((rec) =>
    isMultiSelect ? rec[item] || [] : rec[item]
  );

  const uniqueValues = [
    ...new Set(values.flatMap((v) => (Array.isArray(v) ? v : [v]))),
  ].filter((v) => v !== undefined && v !== null && v !== "");

  const hasEmpty = values.some((v) => (isMultiSelect ? v.length === 0 : !v));

  if (uniqueValues.length === 0) return "black"; // all empty → black
  if (uniqueValues.length > 1) return "red"; // multiple different values → red
  if (hasEmpty) return "red"; // one empty, one filled → red
  return "black"; // all same non-empty → black
};
